/**
 * Authentication Service
 * Handles login, signup, logout, and user management
 */

import api from './api';

class AuthService {
  /**
   * Login user
   * @param {string} email - User email
   * @param {string} password - User password
   * @returns {Promise<Object>} User data and tokens
   */
  async login(email, password) {
    const response = await api.post('/auth/login', {
      login: email,
      password,
    }, { includeAuth: false });

    if (response.success && response.data) {
      // Store tokens
      api.setToken(response.data.tokens.accessToken);
      api.setRefreshToken(response.data.tokens.refreshToken);

      // Store user data
      api.setUser(response.data.user);

      return response.data;
    }

    throw new Error(response.message || 'Login failed');
  }

  /**
   * Register new user
   * @param {Object} userData - User registration data
   * @returns {Promise<Object>} User data
   */
  async signup(userData) {
    const payload = {
      firstName: userData.fullName.split(' ')[0],
      lastName: userData.fullName.split(' ').slice(1).join(' ') || userData.fullName.split(' ')[0],
      email: userData.email,
      password: userData.password,
    };

    // Add company name if provided
    if (userData.company) {
      payload.companyName = userData.company;
    }

    // Add company address if provided
    if (userData.companyStreet || userData.companyCity || userData.companyState || userData.companyZip) {
      payload.companyAddress = {};
      if (userData.companyStreet) payload.companyAddress.street = userData.companyStreet;
      if (userData.companyCity) payload.companyAddress.city = userData.companyCity;
      if (userData.companyState) payload.companyAddress.state = userData.companyState;
      if (userData.companyZip) payload.companyAddress.zipCode = userData.companyZip;
    }

    const response = await api.post('/auth/register', payload, { includeAuth: false });

    if (response.success && response.data) {
      return response.data;
    }

    throw new Error(response.message || 'Registration failed');
  }

  /**
   * Logout user
   */
  async logout() {
    try {
      // Only call logout endpoint if user has a token
      // This prevents unnecessary API calls and infinite loops when user is not authenticated
      const token = api.getToken();
      if (token) {
        await api.post('/auth/logout');
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Always clear local data
      api.removeTokens();
    }
  }

  /**
   * Get current user
   * @returns {Promise<Object>} Current user data
   */
  async getCurrentUser() {
    const response = await api.get('/auth/me');

    if (response.success && response.data) {
      api.setUser(response.data.user);
      return response.data.user;
    }

    throw new Error('Failed to get current user');
  }

  /**
   * Check if user is authenticated
   * @returns {boolean}
   */
  isAuthenticated() {
    return !!api.getToken();
  }

  /**
   * Get stored user data
   * @returns {Object|null}
   */
  getUser() {
    return api.getUser();
  }

  /**
   * Refresh access token
   * @returns {Promise<string>} New access token
   */
  async refreshToken() {
    const refreshToken = api.getRefreshToken();

    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await api.post('/auth/refresh-token', {
      refreshToken,
    }, { includeAuth: false });

    if (response.success && response.data) {
      api.setToken(response.data.accessToken);
      return response.data.accessToken;
    }

    throw new Error('Token refresh failed');
  }

  /**
   * Request password reset
   * @param {string} email - User email
   * @returns {Promise<Object>}
   */
  async forgotPassword(email) {
    const response = await api.post('/auth/forgot-password', {
      email,
    }, { includeAuth: false });

    return response;
  }

  /**
   * Reset password with token
   * @param {string} token - Reset token
   * @param {string} password - New password
   * @returns {Promise<Object>}
   */
  async resetPassword(token, password) {
    const response = await api.post('/auth/reset-password', {
      token,
      password,
    }, { includeAuth: false });

    return response;
  }

  /**
   * Update user profile
   * @param {Object} userData - Updated user data
   * @returns {Promise<Object>}
   */
  async updateProfile(userData) {
    const response = await api.put('/auth/profile', userData);

    if (response.success && response.data) {
      // Update stored user data
      const currentUser = api.getUser();
      const updatedUser = response.data.user || response.data;
      api.setUser({ ...currentUser, ...updatedUser });
      return updatedUser;
    }

    throw new Error('Profile update failed');
  }

  /**
   * Change password
   * @param {string} currentPassword - Current password
   * @param {string} newPassword - New password
   * @returns {Promise<Object>}
   */
  async changePassword(currentPassword, newPassword) {
    const response = await api.put('/auth/change-password', {
      currentPassword,
      newPassword,
    });

    return response;
  }

  /**
   * Update company information
   * @param {Object} companyData - Company data to update
   * @returns {Promise<Object>}
   */
  async updateCompany(companyData) {
    const response = await api.put('/auth/company', companyData);

    if (response.success && response.data) {
      return response.data.company;
    }

    throw new Error(response.message || 'Company update failed');
  }

  /**
   * Upload avatar
   * @param {FormData} formData - Form data containing avatar file
   * @returns {Promise<Object>}
   */
  async uploadAvatar(formData) {
    // Don't set Content-Type header - browser will automatically set it with boundary
    const response = await api.post('/auth/avatar', formData);

    if (response.success && response.data) {
      // Update stored user data
      const currentUser = api.getUser();
      const updatedUser = response.data.user || response.data;
      api.setUser({ ...currentUser, ...updatedUser });
      return response.data;
    }

    throw new Error(response.message || 'Avatar upload failed');
  }

  /**
   * Verify email with OTP
   * @param {string} email - User email
   * @param {string} otp - Verification OTP
   * @returns {Promise<Object>}
   */
  async verifyEmailOTP(email, otp) {
    const response = await api.post('/auth/verify-email-otp', {
      email,
      otp,
    }, { includeAuth: false });

    return response;
  }

  /**
   * Resend verification email/OTP
   * @param {string} email - User email
   * @param {string} method - 'link' or 'otp'
   * @returns {Promise<Object>}
   */
  async resendVerification(email, method = 'otp') {
    const response = await api.post('/auth/resend-verification', {
      email,
      method,
    }, { includeAuth: false });

    return response;
  }
}

const authService = new AuthService();
export default authService;
